import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Plus, Pencil, Trash2, Loader2, Save, Eye, EyeOff, ArrowUp, ArrowDown, X, ExternalLink,
  Bold, Link as LinkIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { AdminAPI } from '../../lib/api';
import ImageUploader from '../../components/admin/ImageUploader';

const CENTRAL_IDEA = 'Proof Nation does not simply sell printing technology. Proof Nation makes it work for the operation.';

const BLOCK_TYPES = [
  { value: 'p', label: 'Paragraph' },
  { value: 'h2', label: 'Heading' },
  { value: 'h3', label: 'Subheading' },
  { value: 'ul', label: 'Bullet list' },
  { value: 'ol', label: 'Numbered list' },
  { value: 'quote', label: 'Quote / callout' },
  { value: 'formula', label: 'Formula' },
  { value: 'image', label: 'Image' },
];
const LIST_TYPES = ['ul', 'ol'];
const INLINE_HINT = 'Tip: **bold** and [text](https://link.com) or [text](/impoproof) work inside text.';

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 80);

const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const newBlock = (type = 'p') => ({ _uid: uid(), type, text: '', itemsText: '', src: '', alt: '', caption: '' });

const EMPTY = {
  title: '', slug: '', audience: '', excerpt: '', author: 'Proof Nation Technical Team',
  read_minutes: 6, order: 0, published: true, meta_description: '', primary_keyword: '',
  hero_type: 'photo', hero_image: '', hero_diagram: 'color-workflow', og_image: '',
  cta_label: '', cta_href: '/contact', cta_description: '',
  dl_label: '', dl_kind: 'checklist', dl_title: '', dl_intro: '', dl_itemsText: '',
  blocks: [newBlock('p')],
};

const toForm = (p) => ({
  title: p.title || '', slug: p.slug || '', audience: p.audience || '', excerpt: p.excerpt || '',
  author: p.author || 'Proof Nation Technical Team', read_minutes: p.read_minutes || 6,
  order: p.order || 0, published: p.published !== false,
  meta_description: p.meta_description || '', primary_keyword: p.primary_keyword || '',
  hero_type: p.hero_type || 'photo', hero_image: p.hero_image || '',
  hero_diagram: p.hero_diagram || 'color-workflow', og_image: p.og_image || '',
  cta_label: p.cta?.label || '', cta_href: p.cta?.href || '/contact', cta_description: p.cta?.description || '',
  dl_label: p.download?.label || '', dl_kind: p.download?.kind || 'checklist',
  dl_title: p.download?.title || '', dl_intro: p.download?.intro || '',
  dl_itemsText: (p.download?.items || []).join('\n'),
  blocks: (p.sections || []).map((b) => ({
    _uid: uid(),
    type: b.type,
    text: b.text || '',
    itemsText: (b.items || []).join('\n'),
    src: b.src || '', alt: b.alt || '', caption: b.caption || '',
  })),
});

const toPayload = (f) => ({
  title: f.title.trim(),
  slug: (f.slug.trim() || slugify(f.title)),
  audience: f.audience.trim(),
  excerpt: f.excerpt.trim(),
  author: f.author.trim() || 'Proof Nation Technical Team',
  read_minutes: Number(f.read_minutes) || 6,
  order: Number(f.order) || 0,
  published: !!f.published,
  meta_description: f.meta_description.trim(),
  primary_keyword: f.primary_keyword.trim(),
  hero_type: f.hero_type,
  hero_image: f.hero_type === 'photo' ? f.hero_image.trim() : '',
  hero_diagram: f.hero_type === 'diagram' ? f.hero_diagram : '',
  og_image: f.og_image.trim(),
  cta: { label: f.cta_label.trim(), href: f.cta_href.trim() || '/contact', description: f.cta_description.trim() },
  download: {
    label: f.dl_label.trim(),
    kind: f.dl_kind,
    title: f.dl_title.trim(),
    intro: f.dl_intro.trim(),
    items: f.dl_itemsText.split('\n').map((s) => s.trim()).filter(Boolean),
  },
  sections: f.blocks.map((b) => {
    if (b.type === 'image') return { type: 'image', src: (b.src || '').trim(), alt: (b.alt || '').trim(), caption: (b.caption || '').trim() };
    if (LIST_TYPES.includes(b.type)) return { type: b.type, items: b.itemsText.split('\n').map((s) => s.trim()).filter(Boolean) };
    return { type: b.type, text: b.text };
  }),
});

const BlockEditor = ({ blocks, setBlocks }) => {
  const refs = useRef({});
  const update = (i, patch) => setBlocks(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const remove = (i) => setBlocks(blocks.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    setBlocks(next);
  };
  const add = (type) => {
    const base = newBlock(type);
    if (type === 'quote') base.text = CENTRAL_IDEA;
    setBlocks([...blocks, base]);
  };

  const wrap = (i, before, after, fallback) => {
    const ta = refs.current[i];
    const b = blocks[i];
    if (!ta) { update(i, { text: `${b.text || ''}${before}${fallback}${after}` }); return; }
    const s = ta.selectionStart ?? (b.text || '').length;
    const e = ta.selectionEnd ?? s;
    const val = b.text || '';
    const sel = val.slice(s, e) || fallback;
    const next = val.slice(0, s) + before + sel + after + val.slice(e);
    update(i, { text: next });
    requestAnimationFrame(() => { try { ta.focus(); ta.setSelectionRange(s + before.length, s + before.length + sel.length); } catch (_) {} });
  };

  return (
    <div className="space-y-3" data-testid="blog-block-editor">
      {blocks.map((b, i) => (
        <div key={b._uid} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <div className="flex items-center gap-2 mb-2">
            <select
              value={b.type}
              onChange={(e) => update(i, { type: e.target.value })}
              className="h-9 rounded-lg border border-slate-200 px-2 text-xs font-bold text-[#0A2A63]"
              data-testid={`block-type-${i}`}
            >
              {BLOCK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">#{i + 1}</span>
            {!LIST_TYPES.includes(b.type) && b.type !== 'image' && (
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => wrap(i, '**', '**', 'bold text')} title="Bold" className="h-8 w-8 rounded-lg bg-white border border-slate-200 hover:border-[#0A2A63] text-[#0A2A63] flex items-center justify-center" data-testid={`block-bold-${i}`}><Bold className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => wrap(i, '[', '](https://)', 'link text')} title="Insert link" className="h-8 w-8 rounded-lg bg-white border border-slate-200 hover:border-[#0A2A63] text-[#0A2A63] flex items-center justify-center" data-testid={`block-link-${i}`}><LinkIcon className="h-3.5 w-3.5" /></button>
              </div>
            )}
            <div className="ml-auto flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} className="h-8 w-8 rounded-lg hover:bg-slate-200 text-slate-500 flex items-center justify-center"><ArrowUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="h-8 w-8 rounded-lg hover:bg-slate-200 text-slate-500 flex items-center justify-center"><ArrowDown className="h-4 w-4" /></button>
              <button type="button" onClick={() => remove(i)} className="h-8 w-8 rounded-lg hover:bg-red-100 text-red-600 flex items-center justify-center" data-testid={`block-remove-${i}`}><X className="h-4 w-4" /></button>
            </div>
          </div>

          {b.type === 'image' ? (
            <div className="space-y-2">
              <ImageUploader label="Image" value={b.src} onChange={(v) => update(i, { src: v })} testId={`block-image-${i}`} />
              <Input value={b.alt} onChange={(e) => update(i, { alt: e.target.value })} placeholder="Alt text (accessibility)" className="rounded-lg text-sm bg-white h-10" data-testid={`block-alt-${i}`} />
              <Input value={b.caption} onChange={(e) => update(i, { caption: e.target.value })} placeholder="Caption (optional)" className="rounded-lg text-sm bg-white h-10" data-testid={`block-caption-${i}`} />
            </div>
          ) : LIST_TYPES.includes(b.type) ? (
            <Textarea
              value={b.itemsText}
              onChange={(e) => update(i, { itemsText: e.target.value })}
              rows={4}
              placeholder="One item per line"
              className="rounded-lg resize-none text-sm bg-white"
              data-testid={`block-items-${i}`}
            />
          ) : (
            <Textarea
              ref={(el) => { refs.current[i] = el; }}
              value={b.text}
              onChange={(e) => update(i, { text: e.target.value })}
              rows={b.type === 'p' || b.type === 'quote' ? 3 : 2}
              placeholder={b.type === 'formula' ? 'e.g. Reprinted Jobs ÷ Total Jobs × 100' : 'Text…'}
              className="rounded-lg resize-none text-sm bg-white"
              data-testid={`block-text-${i}`}
            />
          )}
        </div>
      ))}
      <p className="text-[11px] text-slate-400">{INLINE_HINT}</p>
      <div className="flex flex-wrap gap-2 pt-1">
        {BLOCK_TYPES.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => add(t.value)}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2A63] hover:border-[#0A2A63]"
            data-testid={`block-add-${t.value}`}
          >
            <Plus className="h-3 w-3" /> {t.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const BlogForm = ({ open, onClose, editing, onSaved }) => {
  const { toast } = useToast();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  useEffect(() => { setF(editing ? toForm(editing) : EMPTY); }, [editing, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast({ title: 'Missing title', description: 'A title is required.' }); return; }
    setSaving(true);
    try {
      const payload = toPayload(f);
      if (editing) await AdminAPI.updateBlogPost(editing.id, payload);
      else await AdminAPI.createBlogPost(payload);
      toast({ title: 'Saved', description: `“${payload.title}” ${editing ? 'updated' : 'created'}` });
      onSaved(); onClose();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not save article.' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="blog-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black text-[#050F2E]">{editing ? 'Edit article' : 'New article'}</DialogTitle>
          <DialogDescription>The download PDF is regenerated from the checklist items each time you save.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-5 mt-2">
          {/* Basics */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title *">
              <Input value={f.title} onChange={(e) => { const v = e.target.value; setF((p) => ({ ...p, title: v, slug: (!editing && (!p.slug || p.slug === slugify(p.title))) ? slugify(v) : p.slug })); }} className="h-11 rounded-xl" data-testid="bf-title" />
            </Field>
            <Field label="Slug (URL)"><Input value={f.slug} onChange={(e) => set('slug', e.target.value)} className="h-11 rounded-xl font-mono text-sm" data-testid="bf-slug" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Audience"><Input value={f.audience} onChange={(e) => set('audience', e.target.value)} className="h-11 rounded-xl" data-testid="bf-audience" /></Field>
            <Field label="Author"><Input value={f.author} onChange={(e) => set('author', e.target.value)} className="h-11 rounded-xl" data-testid="bf-author" /></Field>
            <Field label="Read minutes"><Input type="number" value={f.read_minutes} onChange={(e) => set('read_minutes', e.target.value)} className="h-11 rounded-xl" /></Field>
          </div>
          <Field label="Excerpt (blog card + preview)"><Textarea value={f.excerpt} onChange={(e) => set('excerpt', e.target.value)} rows={2} className="rounded-xl resize-none" data-testid="bf-excerpt" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Meta description (SEO)"><Textarea value={f.meta_description} onChange={(e) => set('meta_description', e.target.value)} rows={2} className="rounded-xl resize-none text-sm" /></Field>
            <Field label="Primary keyword"><Input value={f.primary_keyword} onChange={(e) => set('primary_keyword', e.target.value)} className="h-11 rounded-xl" /></Field>
          </div>

          {/* Hero */}
          <div className="rounded-xl border border-slate-200 p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Hero visual</p>
            <div className="flex items-center gap-6 mb-3">
              <label className="flex items-center gap-2 text-sm font-semibold"><input type="radio" checked={f.hero_type === 'photo'} onChange={() => set('hero_type', 'photo')} data-testid="bf-hero-photo" /> Photo</label>
              <label className="flex items-center gap-2 text-sm font-semibold"><input type="radio" checked={f.hero_type === 'diagram'} onChange={() => set('hero_type', 'diagram')} data-testid="bf-hero-diagram" /> Own diagram</label>
            </div>
            {f.hero_type === 'photo' ? (
              <ImageUploader label="Hero image" value={f.hero_image} onChange={(v) => set('hero_image', v)} testId="bf-hero-image" />
            ) : (
              <Field label="Diagram">
                <select value={f.hero_diagram} onChange={(e) => set('hero_diagram', e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-semibold" data-testid="bf-hero-diagram-select">
                  <option value="color-workflow">Color workflow (multi-site)</option>
                  <option value="device-variance">Device variance (same file)</option>
                  <option value="kpi-scorecard">KPI scorecard</option>
                </select>
              </Field>
            )}
            <div className="mt-3">
              <ImageUploader label="Social share image (og:image, optional — used for LinkedIn / WhatsApp preview)" value={f.og_image} onChange={(v) => set('og_image', v)} testId="bf-og-image" />
            </div>
          </div>

          {/* Body blocks */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Article body</p>
            <BlockEditor blocks={f.blocks} setBlocks={(b) => set('blocks', b)} />
          </div>

          {/* CTA */}
          <div className="rounded-xl border border-slate-200 p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Call to action</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA label"><Input value={f.cta_label} onChange={(e) => set('cta_label', e.target.value)} className="h-11 rounded-xl" data-testid="bf-cta-label" /></Field>
              <Field label="CTA link (/contact, /impoproof or #lead-form)"><Input value={f.cta_href} onChange={(e) => set('cta_href', e.target.value)} className="h-11 rounded-xl font-mono text-sm" data-testid="bf-cta-href" /></Field>
            </div>
            <div className="mt-3"><Field label="CTA description"><Textarea value={f.cta_description} onChange={(e) => set('cta_description', e.target.value)} rows={2} className="rounded-xl resize-none" /></Field></div>
          </div>

          {/* Download */}
          <div className="rounded-xl border border-slate-200 p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Downloadable checklist / scorecard</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Kind">
                <select value={f.dl_kind} onChange={(e) => set('dl_kind', e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-semibold" data-testid="bf-dl-kind">
                  <option value="checklist">Checklist</option>
                  <option value="scorecard">Scorecard</option>
                </select>
              </Field>
              <div className="col-span-2"><Field label="PDF title"><Input value={f.dl_title} onChange={(e) => set('dl_title', e.target.value)} className="h-11 rounded-xl" data-testid="bf-dl-title" /></Field></div>
            </div>
            <div className="mt-3"><Field label="Button label"><Input value={f.dl_label} onChange={(e) => set('dl_label', e.target.value)} className="h-11 rounded-xl" data-testid="bf-dl-label" /></Field></div>
            <div className="mt-3"><Field label="Intro line"><Input value={f.dl_intro} onChange={(e) => set('dl_intro', e.target.value)} className="h-11 rounded-xl" /></Field></div>
            <div className="mt-3"><Field label="Checklist items (one per line)"><Textarea value={f.dl_itemsText} onChange={(e) => set('dl_itemsText', e.target.value)} rows={5} className="rounded-xl resize-none font-mono text-xs" data-testid="bf-dl-items" /></Field></div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input type="checkbox" checked={f.published} onChange={(e) => set('published', e.target.checked)} data-testid="bf-published" /> Published
            </label>
            <div className="flex items-center gap-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Order</Label>
              <Input type="number" value={f.order} onChange={(e) => set('order', e.target.value)} className="h-9 w-20 rounded-lg" data-testid="bf-order" />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full h-11 px-6 font-bold">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-11 px-6 font-bold" data-testid="bf-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AdminBlog = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await AdminAPI.listBlog()); }
    catch { toast({ title: 'Error', description: 'Could not load articles.' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async (p) => {
    try { await AdminAPI.setBlogPublished(p.id, !p.published); toast({ title: p.published ? 'Unpublished' : 'Published', description: p.title }); load(); }
    catch { toast({ title: 'Error', description: 'Could not update.' }); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete “${p.title}”? This cannot be undone.`)) return;
    try { await AdminAPI.deleteBlogPost(p.id); toast({ title: 'Deleted', description: p.title }); load(); }
    catch { toast({ title: 'Error', description: 'Could not delete.' }); }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black text-[#050F2E] tracking-tight">Blog</h1>
          <p className="text-slate-500 mt-1">{items.length} articles · create, edit and publish without code</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-11 px-6 font-bold" data-testid="admin-new-article">
          <Plus className="h-4 w-4 mr-2" /> New article
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left p-4">#</th>
                <th className="text-left p-4">Article</th>
                <th className="text-left p-4">Audience</th>
                <th className="text-left p-4">Status</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`admin-blog-row-${p.slug}`}>
                  <td className="p-4 text-slate-400 font-mono">{p.order}</td>
                  <td className="p-4">
                    <p className="font-bold text-[#050F2E] leading-tight">{p.title}</p>
                    <p className="text-xs text-slate-400 font-mono">/blog/{p.slug}</p>
                  </td>
                  <td className="p-4 text-slate-600">{p.audience}</td>
                  <td className="p-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${p.published ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {p.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="View" className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center"><ExternalLink className="h-4 w-4" /></a>
                      <button onClick={() => togglePublish(p)} title={p.published ? 'Unpublish' : 'Publish'} className="h-8 w-8 rounded-lg hover:bg-slate-100 text-[#0A2A63] flex items-center justify-center" data-testid={`blog-toggle-${p.slug}`}>
                        {p.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button onClick={() => { setEditing(p); setFormOpen(true); }} title="Edit" className="h-8 w-8 rounded-lg hover:bg-slate-100 text-[#0A2A63] flex items-center justify-center" data-testid={`blog-edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(p)} title="Delete" className="h-8 w-8 rounded-lg hover:bg-red-100 text-red-600 flex items-center justify-center" data-testid={`blog-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BlogForm open={formOpen} editing={editing} onClose={() => setFormOpen(false)} onSaved={load} />
    </div>
  );
};

export default AdminBlog;
